import React, { useEffect, useState } from 'react';
import { Box, CircularProgress, Dialog, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import axiosConfig from 'src/Resources/axiosConfig';
import { ProductResource } from 'src/interfaces/ProductResourceTypes';

interface Props {
  resource: ProductResource | null;
  onClose: () => void;
}

// Modal player for a guidance video. Loads the file as an authed blob (mirrors
// DocumentList) so preload="none" holds until the user opens it, then plays it
// full-size. Dialog is fullWidth => responsive on phones.
function OnSiteVideoLightbox({ resource, onClose }: Props) {
  const [url, setUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!resource) {
      setUrl(null);
      return;
    }
    let objectUrl: string | null = null;
    let cancelled = false;
    setLoading(true);
    axiosConfig
      .get('/DownloadDocument', { params: { id: resource.id }, responseType: 'blob' })
      .then((res) => {
        if (cancelled) return;
        objectUrl = window.URL.createObjectURL(
          new Blob([res.data], { type: resource.contentType })
        );
        setUrl(objectUrl);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
      if (objectUrl) window.URL.revokeObjectURL(objectUrl);
    };
  }, [resource]);

  return (
    <Dialog open={!!resource} onClose={onClose} maxWidth="lg" fullWidth>
      <Box sx={{ position: 'relative', backgroundColor: 'common.black' }}>
        <IconButton
          onClick={onClose}
          sx={{ position: 'absolute', top: 8, right: 8, color: 'common.white', zIndex: 1 }}
        >
          <CloseIcon />
        </IconButton>
        {loading && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: 240
            }}
          >
            <CircularProgress sx={{ color: 'common.white' }} />
          </Box>
        )}
        {url && (
          <Box
            component="video"
            controls
            autoPlay
            src={url}
            sx={{ width: '100%', maxHeight: '85vh', display: 'block' }}
          />
        )}
      </Box>
    </Dialog>
  );
}

export default OnSiteVideoLightbox;
